import { registerSW } from "virtual:pwa-register";
import { logError } from "./hooks/useErrorLogging";
import { isDevEnvironment } from "./lib/environment";
import { isUpdateSafeNow, shouldSelfHeal } from "./lib/updateSafety";

const SELF_HEAL_KEY = 'sw-self-heal-build';

/**
 * Escape hatch for "pinned" PWAs. Observed on Lovable hosting: a publish can
 * leave /sw.js stale at the origin while index.html and version.json are new —
 * the SW update check then finds "no change" forever and users stay on an old
 * build for weeks. When version.json (network-only) reports a different build
 * than the running shell AND the normal SW path has produced nothing, we
 * unregister the SW, drop the caches and reload once per target build.
 */
async function selfHealIfPinned(currentBuild: string): Promise<void> {
    try {
        if (!('serviceWorker' in navigator)) return;
        const res = await fetch(`/version.json?cb=${Date.now()}`, { cache: 'no-store' });
        if (!res.ok) return;
        const { build } = (await res.json()) as { build?: string };

        const reg = await navigator.serviceWorker.getRegistration();
        await reg?.update().catch(() => undefined);
        const pending = !!(reg?.waiting || reg?.installing);

        const attempted = (() => {
            try { return localStorage.getItem(SELF_HEAL_KEY); } catch { return null; }
        })();
        if (!shouldSelfHeal(build, currentBuild, attempted, pending)) return;
        if (!isUpdateSafeNow()) return; // never reload mid-login

        console.warn(`[SW] Pinned on ${currentBuild} while server has ${build} and sw.js is stale — self-healing.`);
        logError('[PWA] SELF_HEAL_STALE_SW', { runningBuild: currentBuild, serverBuild: build });
        try { localStorage.setItem(SELF_HEAL_KEY, build!); } catch { /* best effort */ }

        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
        if ('caches' in window) {
            const keys = await caches.keys();
            await Promise.all(keys.map((k) => caches.delete(k)));
        }
        window.location.reload();
    } catch {
        /* offline or fetch failure — try again on a later check */
    }
}

export function initPWA() {
    // Disable PWA logic completely on native platforms (Android/iOS)
    // as they are handled via the native app store updates and don't need SW refreshes
    if ((window as any).Capacitor?.isNativePlatform()) {
        console.log('[PWA] Native platform detected, skipping Service Worker registration');
        return;
    }

    // ?sw=off — manual kill-switch (LOV-2 review follow-up): routes into the
    // same unregister+cache-wipe as dev/preview, an escape hatch for a pinned
    // shell on ANY hostname (e.g. future preview domains we don't recognize).
    const swOff = new URLSearchParams(window.location.search).get('sw') === 'off';

    if (import.meta.env.DEV || isDevEnvironment() || swOff) {
        // Avoid SW caching in dev/preview (can trap old bundles and make the preview look stale).
        const cleanupKey = 'preview-sw-cleanup-reloaded';
        console.log('[PWA] Dev/preview environment detected, skipping Service Worker registration');

        Promise.all([
            'serviceWorker' in navigator
                ? navigator.serviceWorker.getRegistrations().then(async (registrations) => {
                    await Promise.all(registrations.map((registration) => registration.unregister()));
                    return registrations.length > 0;
                })
                : Promise.resolve(false),
            'caches' in window
                ? caches.keys().then(async (cacheNames) => {
                    await Promise.all(cacheNames.map((cacheName) => caches.delete(cacheName)));
                    return cacheNames.length > 0;
                })
                : Promise.resolve(false),
        ]).then(([hadServiceWorkers, hadCaches]) => {
            if (!hadServiceWorkers && !hadCaches) return;

            try {
                if (!sessionStorage.getItem(cleanupKey)) {
                    sessionStorage.setItem(cleanupKey, '1');
                    window.location.reload();
                }
            } catch {
                window.location.reload();
            }
        }).catch((error) => {
            console.warn('[PWA] Failed to clean preview caches:', error);
        });

        try {
            localStorage.removeItem('ack-build');
        } catch {
            // Ignore storage access issues in preview cleanup path.
        }

        try {
            localStorage.removeItem('app-build-id');
        } catch {
            // Ignore storage access issues in preview cleanup path.
        }
    } else {
        const ACK_KEY = 'ack-build';
        const currentBuild = typeof __APP_BUILD__ !== 'undefined' ? __APP_BUILD__ : 'unknown';

        const updateSW = registerSW({
            immediate: true,
            onRegisteredSW(swUrl, registration) {
                console.log('[SW] Registered:', swUrl);
                console.log('Service worker registered', {
                    type: 'pwa_diagnostic',
                    event: 'sw_registered',
                    swUrl,
                    scope: registration?.scope,
                });

                // Check for updates periodically (every 15 minutes)…
                setInterval(() => {
                    registration?.update().catch((err) => {
                        console.debug('[SW] Background update failed (likely offline):', err);
                    });
                }, 15 * 60 * 1000);

                // …and whenever the app returns to the foreground (throttled).
                // Mobile browsers throttle background timers, so a phone that
                // only opens the app briefly can otherwise stay on a stale
                // shell for weeks — the cause of "old version + broken login"
                // reports while the server already runs the latest build.
                let lastVisibilityCheck = 0;
                document.addEventListener('visibilitychange', () => {
                    if (document.visibilityState !== 'visible') return;
                    const now = Date.now();
                    if (now - lastVisibilityCheck < 5 * 60 * 1000) return;
                    lastVisibilityCheck = now;
                    registration?.update().catch(() => undefined);
                    void selfHealIfPinned(currentBuild);
                });

                // Pinned-PWA self-heal: give the normal update path 25s after
                // load, then verify the running build against the server.
                setTimeout(() => void selfHealIfPinned(currentBuild), 25_000);
            },
            onRegisterError(error) {
                console.error('[SW] Registration failed:', error);
                logError('Service worker registration failed', {
                    type: 'pwa_diagnostic',
                    event: 'sw_error',
                    errorMessage: error instanceof Error ? error.message : String(error),
                    errorStack: error instanceof Error ? error.stack : undefined,
                });
            },
            onNeedRefresh() {
                // Check if the user already acknowledged this exact build
                const ackedBuild = localStorage.getItem(ACK_KEY);
                if (ackedBuild === currentBuild) {
                    // Same build already applied — silently activate the SW without bothering the user.
                    // NEVER silently reload while OAuth tokens are in the URL or a login just
                    // started: that reload ate the callback on stale shells (login bounced
                    // back to the login screen). Skip; the waiting SW applies on a safe load.
                    if (!isUpdateSafeNow()) {
                        console.log('[SW] Update pending but login in progress — deferring.');
                        return;
                    }
                    console.log('[SW] Build already acknowledged, silently updating...');
                    updateSW(true);
                    return;
                }

                // Genuinely new build — notify the user once
                console.log('[SW] New version available, notifying UI...');
                localStorage.setItem(ACK_KEY, currentBuild);

                window.dispatchEvent(new CustomEvent('app-update-available', {
                    detail: {
                        updateServiceWorker: async () => {
                            sessionStorage.setItem('app-just-updated', 'true');
                            await updateSW(true);
                        }
                    }
                }));
            },
        });
    }
}
